import React, { useEffect, useMemo } from 'react';
import isNil from 'lodash/isNil';
import minValue from 'lodash/min';
import maxValue from 'lodash/max';
// import { colord, HsvColor } from 'colord';
// import { ColorBox } from 'material-ui-color';
// import rgbHex from 'rgb-hex';
// import ShadeSlider from '@uiw/react-color-shade-slider';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import Slider from '@material-ui/core/Slider';
import TextField from '@material-ui/core/TextField';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';

import TagLabel from 'components/TagLabel';
import useViewport from 'hooks/useViewports';
import { isBlank } from 'utils/helpers-ts';

import ZoomImage from './ZoomImage';
import ImgixBuilder from './ImgixBuilder';
import { IImage } from './ImageSorter';

const useStyles = makeStyles(() => ({
  paperMd: { minWidth: 900, minHeight: 650 },
}));
interface IEditImageSliderProps {
  value?: number;
  setValue: (value: number) => void;
  title?: string;
  min?: number;
  max?: number;
  step?: number;
}

const EditImageSlider = ({ value, setValue, title, min, max, step }: IEditImageSliderProps) => {
  const { mdUp } = useViewport();
  const [localValue, setLocalValue] = React.useState<string | number>(String(value ?? ''));

  const handleChange = (event: any, newValue: number | number[]) => {
    let value = 0;
    if (Array.isArray(newValue)) {
      if (newValue.length > 0) {
        value = newValue[0];
      }
    } else {
      value = newValue;
    }
    setValue(value);
  };
  const values = [];
  if (!isNil(min)) values.push(min);
  if (!isNil(max)) values.push(max);
  if (!values.includes(0)) values.push(0);
  const marks = values.sort().map(x => ({ value: x, label: x }));

  useEffect(() => {
    setLocalValue(String(value ?? ''));
  }, [value]);

  return (
    <Grid container={true} spacing={2} style={{ marginTop: 0, marginLeft: mdUp ? 10 : 0 }}>
      <Grid item={true} xs={mdUp ? undefined : 12} style={{ width: 80 }}>
        {title}
      </Grid>
      <Grid item={true} xs={mdUp}>
        <Slider
          defaultValue={0}
          step={step || 1}
          min={minValue(values)}
          max={maxValue(values)}
          value={value}
          onChange={handleChange}
          style={{ width: 200 }}
          marks={marks}
        />
      </Grid>
      <Grid item={true} style={{ padding: 0, marginLeft: mdUp ? 0 : 30 }}>
        <TextField
          value={localValue}
          style={{
            width: 55,
          }}
          onChange={event => {
            const newValue = event.target.value || '';
            let currentValue = newValue
              .trim()
              .replace(/[^0-9+-]/g, '')
              .replace(/-+/g, '-');
            if (currentValue === '0-') {
              currentValue = '-';
            }
            const formattedValue = parseInt(currentValue, 10);
            if (isNaN(formattedValue)) {
              setLocalValue(currentValue);
            } else {
              const validValue = Math.max(Math.min(formattedValue, max ?? Infinity), min ?? -Infinity);
              handleChange(undefined, validValue);
              setLocalValue(String(validValue));
            }
          }}
        />
      </Grid>
    </Grid>
  );
};

interface IEditImageDialogProps {
  open: boolean;
  src?: string;
  url?: string;
  images?: IImage[];
  onClose: () => void;
  onSave: (url?: string) => void;
}

const EditImageDialog = ({ open, src, url, images, onClose, onSave }: IEditImageDialogProps) => {
  const classes = useStyles();
  const [baseUrl, setBaseUrl] = React.useState(url);
  const [currentUrl, setCurrentUrl] = React.useState<string | undefined>(undefined);
  const [contrast, setContrast] = React.useState(0);
  const [vibrance, setVibrance] = React.useState(0);
  const [sharpen, setSharpen] = React.useState(0);

  const [hue, setHue] = React.useState(0);
  const [saturation, setSaturation] = React.useState(0);
  const [brightness, setBrightness] = React.useState(0);

  const [red, setRed] = React.useState(0);
  const [green, setGreen] = React.useState(0);
  const [blue, setBlue] = React.useState(0);
  // const [alpha, setAlpha] = React.useState(0);
  const [fullScreen, setFullScreen] = React.useState(false);

  const urlToSave = useMemo(() => {
    const imgix = ImgixBuilder.parse(baseUrl);
    // force to be [null] when values are "0"
    // -> [ImgixBuilder] will not add these 0 values to the url.
    imgix.brightness = brightness || null;
    imgix.contrast = contrast || null;
    imgix.saturation = saturation || null;
    imgix.vibrance = vibrance || null;
    imgix.sharpen = sharpen || null;
    return imgix.getUrl(false);
  }, [baseUrl, brightness, contrast, saturation, sharpen, vibrance]);

  const isDuplicated = useMemo(() => {
    const imgix = ImgixBuilder.parse(urlToSave);
    const newUrl = imgix.getUrl(false);
    return !!images?.some(item => item.pictureUrl === newUrl);
  }, [images, urlToSave]);

  const handleSave = () => {
    onSave(urlToSave);
  };

  // React.useEffect(() => {
  //     const imgix = ImgixBuilder.parse(url);
  //     setCurrentUrl(imgix.getUrl());
  //     setBrightness(imgix.brightness);
  //     setContrast(imgix.contrast);
  //     setSaturation(imgix.saturation);
  //     setSharpen(imgix.sharpen);

  //     setRed(imgix.red);
  //     setGreen(imgix.green);
  //     setBlue(imgix.blue);
  //     setAlpha(isNil(imgix.alpha) ? 0 : imgix.alpha * 100);
  // }, [url]);

  React.useEffect(() => {
    if (!open) {
      setBaseUrl(undefined);
      setBrightness(0);
      setContrast(0);
      setSaturation(0);
      setSharpen(0);
      setVibrance(0);

      // setRed(0);
      // setGreen(0);
      // setBlue(0);
      // setAlpha(0);
    } else {
      const imgix = ImgixBuilder.parse(url);
      setBaseUrl(imgix.getUrl(false));
      setBrightness(imgix.brightness);
      setContrast(imgix.contrast);
      setSaturation(imgix.saturation);
      setSharpen(imgix.sharpen);
      setVibrance(imgix.vibrance);

      // setRed(imgix.red);
      // setGreen(imgix.green);
      // setBlue(imgix.blue);
      // setAlpha(isNil(imgix.alpha) ? 0 : imgix.alpha * 100);
    }
  }, [open, url]);

  React.useEffect(() => {
    if (!isBlank(baseUrl)) {
      const imgix = ImgixBuilder.parse(baseUrl);
      imgix.brightness = brightness || null;
      imgix.contrast = contrast || null;
      imgix.saturation = saturation || null;
      imgix.vibrance = vibrance || null;
      imgix.sharpen = sharpen || null;
      setCurrentUrl(imgix.getUrl());
    } else {
      setCurrentUrl(undefined);
    }
  }, [baseUrl, brightness, contrast, saturation, vibrance, sharpen]);

  const { mdUp } = useViewport();

  React.useLayoutEffect(() => {
    if (open) {
      setFullScreen(false);
    }
  }, [open]);

  return (
    <Dialog
      fullScreen={fullScreen || !mdUp}
      open={open}
      classes={{ paper: mdUp ? classes.paperMd : '' }}
      onClick={event => event.stopPropagation()}
    >
      <DialogTitle>Edit image</DialogTitle>
      <DialogContent>
        <Grid container={true}>
          <Grid item={true} xs={fullScreen || !mdUp ? 12 : undefined} style={{ maxWidth: '100%' }}>
            <ZoomImage
              maxWidth={400}
              maxHeight={400}
              fullScreen={fullScreen}
              src={src?.startsWith('blob') ? src : currentUrl}
              onToggleFullScreen={setFullScreen}
            />
          </Grid>
          <Grid item={true} style={{ display: fullScreen ? 'none' : undefined }}>
            <EditImageSlider value={brightness} setValue={setBrightness} title="Brightness" min={-100} max={100} />
            <EditImageSlider value={contrast} setValue={setContrast} title="Contrast" min={-100} max={100} />
            <EditImageSlider value={saturation} setValue={setSaturation} title="Saturation" min={-100} max={100} />
            <EditImageSlider value={vibrance} setValue={setVibrance} title="Vibrance" min={-100} max={100} />
            <EditImageSlider value={sharpen} setValue={setSharpen} title="Sharpen" min={0} max={100} />
            {/* <EditImageSlider
                            value={red}
                            setValue={setRed}
                            title="Red"
                            min={0}
                            max={0xff}
                        />
                        <EditImageSlider
                            value={green}
                            setValue={setGreen}
                            title="Green"
                            min={0}
                            max={0xff}
                        />
                        <EditImageSlider
                            value={blue}
                            setValue={setBlue}
                            title="Blue"
                            min={0}
                            max={0xff}
                        />
                        <EditImageSlider
                            value={alpha}
                            setValue={setAlpha}
                            title="Alpha"
                            min={0}
                            max={100}
                        /> */}
            {/* <ColorBox
                            defaultValue="transparent"
                            value={color}
                            inputFormats={[]}
                            onChange={(color) => {
                                // setRed(color.rgb[0]);
                                // setGreen(color.rgb[1]);
                                // setBlue(color.rgb[2]);
                                // setAlpha(color.alpha);
                            }}
                        /> */}
            {/* <Grid container={true} spacing={2} style={{ marginTop: 10, marginLeft: 10 }}>
                            <Grid item={true} style={{width: 80}}>Red</Grid>
                            <Grid item={true} xs={true}>
                                <ShadeSlider
                                    hsva={{
                                        h: 0, 
                                        s: colord({r: red, g: 0, b: 0}).toHsv().s, 
                                        v: colord({r: red, g: 0, b: 0}).toHsv().v, 
                                        a: 1,
                                    }}
                                    style={{ marginTop: 10, marginLeft: 10 }}
                                    onChange={(newShade) => {
                                        const color: HsvColor = {
                                            h: 0,
                                            s: newShade.s,
                                            v: newShade.v,
                                        };
                                        const { r } = colord(color).toRgb();
                                        setRed(r);
                                    }}
                                />
                            </Grid>
                            <Grid item={true} style={{ padding: 0 }}>
                                <TextField
                                    value={red}
                                    style={{
                                        width: 42,
                                    }}
                                />
                            </Grid>
                        </Grid> */}
            {/* <ShadeSlider
                            hsva={{h: 0, s: 100, v: 100, a: 1}}
                            style={{ marginTop: 10, marginLeft: 10 }}
                        /> */}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box display="flex" flexDirection="column" alignItems="center" width="100%" gridGap={8}>
          {isDuplicated && (
            <TagLabel type="warn" label="Det finns redan en version av denna bild med dessa inställningar" />
          )}
          <Box display="flex" justifyContent="flex-end" width="100%">
            <Button onClick={onClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleSave} color="primary" autoFocus disabled={isDuplicated}>
              Save
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditImageDialog;
